// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css } from '@emotion/css';
import { Theme, useTheme } from '@mui/material';
import { BiSolidCheckCircle } from 'react-icons/bi';

import { TileProps } from '@components/Timeline/Tile/Tile';

// TYPES ---------------------------------------------------------------- //

export interface TileGroupProps {
  children?: React.ReactNode;
  endGapWidth?: number;
  isSelected?: boolean;
  isSelectedOverlayColor?: string;
  isSelectedOverlayIconColor?: string;
  onClick?: () => void;
  startGapWidth?: number;
  zIndex?: number;
  zoom?: number;
}

// COMPONENT ---------------------------------------------------------------- //

const TileGroup = ({
  children,
  endGapWidth = 0,
  isSelected = false,
  isSelectedOverlayColor = '#404860',
  isSelectedOverlayIconColor = '#ffffff',
  onClick = () => {},
  startGapWidth = 0,
  zIndex = 0,
  zoom = 4,
}: TileGroupProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const childrenArray = React.Children.toArray(children);
  const childrenLength = childrenArray.length;

  return (
    <div
      className={cls.tileGroup}
      style={{ marginLeft: startGapWidth * zoom, marginRight: endGapWidth * zoom }}
      onClick={onClick}
    >
      {/* SELECTED OVERLAY */}
      <div className={cls.selectedOverlayContainer} style={{ zIndex: zIndex + 10 }}>
        <div
          className={cls.selectedOverlayBackground}
          style={{ backgroundColor: isSelectedOverlayColor, opacity: isSelected ? 0.667 : 0 }}
        />
        <div
          className={cls.selectedOverlayBorder}
          style={{ outlineColor: isSelectedOverlayColor, opacity: isSelected ? 1 : 0 }}
        />
        <BiSolidCheckCircle
          className={cls.selectedOverlayIcon}
          style={{ color: isSelectedOverlayIconColor, opacity: isSelected ? 1 : 0, zIndex: zIndex + 11 }}
          size={40}
        />
      </div>

      {/* MOVE CHILDREN */}
      {React.Children.map(children, (child, i) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<TileProps>, {
            roundLeftCorners: i === 0,
            roundRightCorners: i === childrenLength - 1,
            zIndex: zIndex + childrenLength - i,
          });
        }
        return child;
      })}
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  tileGroup: css`
    position: relative;
    display: flex;
    border-radius: 8px;
    outline-width: 4px;
    outline-style: solid;
    outline-color: transparent;
    transition: outline 0.25s;
    &:hover {
      outline-color: ${theme.palette.utility.main}40;
    }
  `,

  selectedOverlayContainer: css`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 8px;
    user-select: none;
    pointer-events: none;
  `,
  selectedOverlayBackground: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 8px;
    background-color: #000000;
    transition: all 0.1s ease-in-out;
  `,
  selectedOverlayBorder: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 8px;
    outline-style: solid;
    outline-width: 4px;
    transition: all 0.1s ease-in-out;
    pointer-events: none;
  `,
  selectedOverlayIcon: css`
    color: ${theme.palette.text.contrastPrimary};
    transition: all 0.1s ease-in-out;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default TileGroup;
